import { STEP_VISUALS } from './stepVisuals';

export type StepAnnotation = { status: string; detail: string };

/**
 * Layered visual for the six-step lifecycle: the active step's screenshot sits in front and the
 * previous one recedes behind it, with a status pill and a detail chip for the active step.
 * Shared by the homepage scroll story and the How it works page.
 */
export default function StepStage({
  active,
  count,
  alts,
  annotations,
  className = '',
}: {
  active: number;
  count: number;
  /** Alt text per step; only the active image is exposed to assistive tech. */
  alts: string[];
  annotations: StepAnnotation[];
  className?: string;
}) {
  const note = annotations[active];
  const icon = STEP_VISUALS[active]?.icon ?? 'ri-checkbox-circle-line';

  return (
    <div className={`relative aspect-[16/10] w-full ${className}`}>
      <div className="absolute -inset-8 rounded-[2rem] bg-blue-500/[0.07] blur-2xl" aria-hidden />
      {STEP_VISUALS.slice(0, count).map((v, i) => {
        const offset = i - active;
        const pose =
          offset === 0
            ? 'z-20 translate-x-0 translate-y-0 scale-100 opacity-100'
            : offset === -1
              ? 'z-10 -translate-x-[7%] -translate-y-[9%] scale-[0.84] opacity-40'
              : offset < -1
                ? 'z-0 -translate-x-[10%] -translate-y-[14%] scale-[0.78] opacity-0'
                : 'z-0 translate-x-[6%] translate-y-[6%] scale-[0.96] opacity-0';
        return (
          <div
            key={v.img}
            className={`absolute inset-0 overflow-hidden rounded-2xl border border-gray-200 bg-slate-50 shadow-2xl shadow-blue-900/10 transition-all duration-700 ease-out ${pose}`}
            aria-hidden={offset !== 0}
          >
            <img src={v.img} alt={offset === 0 ? alts[i] ?? '' : ''} loading={i < 2 ? 'eager' : 'lazy'} className="h-full w-full object-contain" />
          </div>
        );
      })}

      {note && (
        <>
          <div
            key={`status-${active}`}
            className="absolute -top-4 start-6 z-30 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-gray-900 shadow-lg opacity-0 animate-fade-in-up"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
            </span>
            {note.status}
          </div>
          <div
            key={`detail-${active}`}
            className="absolute -bottom-5 end-6 z-30 flex items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-xl opacity-0 animate-fade-in-up animation-delay-200"
          >
            <i className={`${icon} text-lg text-blue-600`} aria-hidden />
            {note.detail}
          </div>
        </>
      )}
    </div>
  );
}
